import { useState } from 'react';

import { CATEGORY_LABELS } from '@/shared/triage/vocabulary';
import { Badge } from '@/shared/ui/Badge';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Select } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { Stat } from '@/shared/ui/Stat';
import { Table, TBody, Td, Th, THead, Tr } from '@/shared/ui/Table';

import {
  accountsOf,
  answeredByText,
  barText,
  formatPercent,
  modesText,
  personText,
  readyRows,
  type ReliabilityView,
  rightText,
  TIER_LABELS,
  verdictText,
  visibleMailboxes,
} from './reliabilityText';
import {
  PROFILE_LABELS,
  type ReliabilityBand,
  type ReliabilityFilters,
  type ReliabilityMailbox,
  type ReliabilityRow,
} from './types';
import { useReliability } from './useAdmin';

const WINDOWS = [
  { days: 30, label: 'Últimos 30 días' },
  { days: 90, label: 'Últimos 90 días' },
  { days: 365, label: 'Último año' },
] as const;

/**
 * Administración → «Fiabilidad» (contract v10.38, ADR 0032 and ADR 0033 of the back): when a
 * category of a mailbox is ready for automatic, and whom to talk to.
 *
 * Superadministrators only (Eloy, 2026-10-03). At the top, the notices: each mailbox and
 * category ready and not yet automatic, with the accounts that have that mailbox, so the
 * superadministrator decides whether to contact them. Below, one card per mailbox with every
 * category, what Activación says today and who answers it. Filters by account and mailbox.
 * Nothing here changes a mode or tells anybody: automatic is switched on in Activación.
 */
export function ReliabilityTab() {
  const [filters, setFilters] = useState<ReliabilityFilters>({ days: 90, current_prompt: false });
  const [view, setView] = useState<ReliabilityView>({ account: '', mailbox: '' });
  const report = useReliability(filters);
  const data = report.data;

  const accounts = data ? accountsOf(data) : [];
  const mailboxes = data ? visibleMailboxes(data, view) : [];
  const names = mailboxes.map((mailbox) => mailbox.mailbox);
  const ready = data ? readyRows(data, names) : [];
  const warnings = data?.rows.filter((row) => row.warning && names.includes(row.mailbox)) ?? [];
  const answered = mailboxes.reduce((sum, mailbox) => sum + mailbox.answered, 0);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-ink-2 text-[14px] leading-relaxed">
        Cuándo una categoría de un buzón está lista para automático, y a quién sugerírselo. De lo
        que alguien respondió, cuánto acertó el sistema en destino y en categoría, y cuánto acierta{' '}
        <strong className="text-ink">como mínimo</strong> con las respuestas que hay. El listón
        depende de lo que cuesta un error: dinero 99 %, lo que se reenvía a una persona 97 %, lo que
        se queda en el buzón 95 %. Aquí no se cambia nada ni se avisa a nadie: el automático se
        enciende en Activación.
      </p>

      <form
        className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-4"
        aria-label="Filtros de la fiabilidad"
        onSubmit={(event) => event.preventDefault()}
      >
        <Select
          label="Periodo"
          value={String(filters.days)}
          onChange={(event) =>
            setFilters((current) => ({ ...current, days: Number(event.target.value) }))
          }
        >
          {WINDOWS.map((window) => (
            <option key={window.days} value={window.days}>
              {window.label}
            </option>
          ))}
        </Select>
        <Select
          label="Cuenta"
          value={view.account}
          onChange={(event) => setView((current) => ({ ...current, account: event.target.value }))}
        >
          <option value="">Todas</option>
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name} ({account.email})
            </option>
          ))}
        </Select>
        <Select
          label="Buzón"
          value={view.mailbox}
          onChange={(event) => setView((current) => ({ ...current, mailbox: event.target.value }))}
        >
          <option value="">Todos</option>
          {(data?.mailboxes ?? []).map((mailbox) => (
            <option key={mailbox.mailbox} value={mailbox.mailbox}>
              {mailbox.mailbox}
            </option>
          ))}
        </Select>
        <label className="text-ink-2 flex items-center gap-2 pb-2 text-[13.5px]">
          <input
            type="checkbox"
            className="accent-accent size-4"
            checked={filters.current_prompt}
            onChange={(event) =>
              setFilters((current) => ({ ...current, current_prompt: event.target.checked }))
            }
          />
          Solo con la versión del prompt de ahora
        </label>
      </form>

      {report.isPending ? (
        <Spinner label="Calculando la fiabilidad…" />
      ) : report.isError ? (
        <ErrorMessage error={report.error}>No se pudo calcular la fiabilidad</ErrorMessage>
      ) : data && data.rows.length === 0 ? (
        <EmptyState
          title="Sin respuestas en este periodo"
          hint="La tabla sale de los «Correcto» y las correcciones. Prueba con un periodo más largo."
        />
      ) : data ? (
        <>
          {!data.actions_enabled ? (
            <Callout compact tone="info" title="El cerrojo está cerrado.">
              Nada actúa solo en ningún buzón, diga lo que diga Activación.
            </Callout>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Respuestas" value={answered} />
            <Stat
              label="Listas para sugerir"
              value={ready.length}
              tone={ready.length ? 'ok' : 'neutral'}
            />
            <Stat
              label="En automático sin llegar"
              value={warnings.length}
              tone={warnings.length ? 'danger' : 'neutral'}
            />
          </div>

          <Notices rows={ready} mailboxes={mailboxes} />

          {mailboxes.length === 0 ? (
            <p className="text-ink-3 text-[14px]">Ningún buzón con esos filtros.</p>
          ) : null}
          {mailboxes.map((mailbox) => (
            <MailboxCard
              key={mailbox.mailbox}
              mailbox={mailbox}
              rows={data.rows.filter((row) => row.mailbox === mailbox.mailbox)}
              bands={data.bands.filter((band) => band.mailbox === mailbox.mailbox)}
            />
          ))}
        </>
      ) : null}
    </div>
  );
}

/** The notices: what is ready and not automatic yet, with whom to talk to. */
function Notices({ rows, mailboxes }: { rows: ReliabilityRow[]; mailboxes: ReliabilityMailbox[] }) {
  return (
    <Card title="Listas para sugerir el automático">
      {rows.length === 0 ? (
        <p className="text-ink-3 text-[14px]">
          Ninguna categoría está lista todavía en estos buzones.
        </p>
      ) : (
        <ul aria-label="Listas para sugerir el automático" className="flex flex-col gap-3">
          {rows.map((row) => {
            const accounts =
              mailboxes.find((mailbox) => mailbox.mailbox === row.mailbox)?.accounts ?? [];
            return (
              <li
                key={`${row.mailbox}-${row.category}`}
                aria-label={`${row.mailbox}: ${CATEGORY_LABELS[row.category]}`}
                className="border-line flex flex-col gap-1 border-b pb-3 text-[13.5px] last:border-0 last:pb-0"
              >
                <p className="text-ink">
                  <strong>{row.mailbox}</strong> · {CATEGORY_LABELS[row.category]}: {rightText(row)}
                  , al menos {formatPercent(row.lower_bound)}
                </p>
                <p className="text-ink-2">Hoy: {modesText(row)}</p>
                <p className="text-ink-2">
                  Contactar:{' '}
                  {accounts.length > 0
                    ? accounts.map(personText).join(' · ')
                    : 'nadie tiene este buzón asignado'}
                </p>
                <p className="text-ink-3">Responde: {answeredByText(row)}</p>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function MailboxCard({
  mailbox,
  rows,
  bands,
}: {
  mailbox: ReliabilityMailbox;
  rows: ReliabilityRow[];
  bands: ReliabilityBand[];
}) {
  const accounts = mailbox.accounts ?? [];
  return (
    <Card title={mailbox.mailbox} padded={false}>
      <div className="text-ink-2 flex flex-col gap-0.5 px-4 pb-3 text-[13px]">
        <p className="text-ink-3">{PROFILE_LABELS[mailbox.profile]}</p>
        <p>
          Lo tienen: {accounts.length > 0 ? accounts.map(personText).join(' · ') : 'nadie asignado'}
        </p>
      </div>
      <Table label={`Fiabilidad de ${mailbox.mailbox}`} minWidth="70rem">
        <THead>
          <Tr>
            <Th>Qué es</Th>
            <Th>Si se equivoca</Th>
            <Th className="text-right">Bien</Th>
            <Th className="text-right">Al menos</Th>
            <Th>Listón</Th>
            <Th>Veredicto</Th>
            <Th>Hoy</Th>
            <Th>Quién responde</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => {
            const verdict = verdictText(row);
            return (
              <Tr key={row.category}>
                <Td className="text-ink font-medium">{CATEGORY_LABELS[row.category]}</Td>
                <Td>{TIER_LABELS[row.tier]}</Td>
                <Td className="text-right tabular-nums">{rightText(row)}</Td>
                <Td className="text-right tabular-nums">{formatPercent(row.lower_bound)}</Td>
                <Td className="text-ink-3 text-[13px]">{barText(row)}</Td>
                <Td>
                  <Badge tone={verdict.tone}>{verdict.text}</Badge>
                </Td>
                <Td className="text-[13px]">
                  <span className={row.warning ? 'text-danger font-medium' : 'text-ink-2'}>
                    {modesText(row)}
                    {row.in_auto ? <span className="block">Actúa sola</span> : null}
                    {row.warning ? <span className="block">{row.warning}</span> : null}
                  </span>
                </Td>
                <Td className="text-ink-2 text-[13px]">{answeredByText(row)}</Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
      {bands.length > 0 ? (
        <div className="border-line border-t px-4 py-3">
          <p className="text-ink-3 mb-2 text-[12px] font-semibold tracking-[0.1em] uppercase">
            Por la confianza del modelo
          </p>
          <ul
            aria-label={`Bandas de confianza de ${mailbox.mailbox}`}
            className="flex flex-wrap gap-x-6 gap-y-1 text-[13px]"
          >
            {bands.map((band) => (
              <li key={band.band} className="text-ink-2">
                <span className="text-ink-3">
                  {band.band === 'regla' ? 'Decidido por una regla' : band.band}:
                </span>{' '}
                {band.ok} de {band.answered} ({formatPercent(band.rate)})
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}
